// The Gallery sheet (G, the bar's Gallery button, ⌘K): every shot and result of the project's showcase,
// beside the scene it belongs to. A card with an app state opens it in the page (the scene as it opens,
// through the store's resetScene, then the link's state as src/app/url.ts applies it); a film or still plays here, with its
// Methods line and the nearest scene. The manifest is src/data/gallery.json (scripts/gallery.py), and
// the media are the public gallery's own files.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ExternalLink, Link, Play, Route, Search, X } from 'lucide-react';
import { useAtlas } from '../app/store.ts';
import { DEV_PARAMS, fromQuery } from '../app/url.ts';
import { SCENARIOS, type ScenarioId } from '../scenarios/index.ts';
import { Button, IconButton, Kbd } from '../ui/primitives.tsx';
import { KIND_LABEL, STATE_LABEL, loadGallery, matches, mediaUrl, type Gallery as GalleryData, type GalleryItem, type GalleryState } from '../data/gallery.ts';
import { copyToClipboard } from './Toast.tsx';
import { linkOf, sceneTour } from './tours.ts';
import './web.css';

/** In a scene's list the interactive cards lead, then the partial ones, then films and figures. */
const RANK: Record<GalleryState, number> = { app: 0, partly: 1, render: 2, figure: 3 };

/** The link as a page: the query with the bar's developer switches (engine=mock on the 2D preview) kept. */
export function linkHref(q: string, search = location.search, pathname = location.pathname): string {
  const next = new URLSearchParams(q);
  const keep = new URLSearchParams(search);
  for (const k of DEV_PARAMS) {
    const v = keep.get(k);
    if (v !== null && !next.has(k)) next.set(k, v);
  }
  const str = next.toString();
  return str ? `${pathname}?${str}` : pathname;
}

/** Open a card's state in the page: the scene as it opens (`resetScene`, even the one on screen; ba-app's #672), then
 *  the link's state on top, as a page load applies it. The sheet closes, and the bar's URL follows the store. A link
 *  to no scene navigates, so the page starts exactly as it says. */
export function openLink(q: string) {
  const params = new URLSearchParams(q.startsWith('?') ? q.slice(1) : q);
  const scene = params.get('scene');
  if (!scene || !SCENARIOS.some((x) => x.id === scene)) {
    location.assign(linkHref(q));
    return;
  }
  const s = useAtlas.getState();
  s.setGallery(false);
  s.resetScene(scene as ScenarioId);
  params.delete('gallery');
  fromQuery(`?${params.toString()}`);
}

/** A card's picture: a still, or a clip's poster with the play mark; the Highlights strip (features/Highlights.tsx) uses it too. */
export function Poster({ g, it }: { g: GalleryData; it: GalleryItem }) {
  const m = it.media;
  const src = mediaUrl(g, m.kind === 'video' ? (m.poster ?? m.src) : m.src);
  return (
    <span className={`gcard-media ${it.fit === 'contain' ? 'contain' : ''}`}>
      <img src={src} alt="" loading="lazy" decoding="async" />
      {m.kind === 'video' ? (
        <span className="gcard-play" aria-hidden="true">
          <Play size={14} fill="currentColor" />
        </span>
      ) : null}
    </span>
  );
}

function Card({ g, it, onOpen }: { g: GalleryData; it: GalleryItem; onOpen: () => void }) {
  return (
    <li>
      <button type="button" className="gcard" data-state={it.state} onClick={onOpen} aria-label={`${it.title}: ${STATE_LABEL[it.state]}`}>
        <Poster g={g} it={it} />
        <span className="gcard-text">
          <span className="gcard-title ba-type-body-strong">{it.title}</span>
          <span className="gcard-line ba-type-small">{it.line}</span>
          <span className="gcard-foot ba-type-caption">
            <span className={`gstate gstate-${it.state}`}>{STATE_LABEL[it.state]}</span>
            <span className="muted">{KIND_LABEL[it.kind]}</span>
          </span>
        </span>
      </button>
    </li>
  );
}

function Detail({ g, it, onBack }: { g: GalleryData; it: GalleryItem; onBack: () => void }) {
  const m = it.media;
  const scene = SCENARIOS.find((sc) => sc.id === it.scene);
  const [first, ...rest] = it.links;
  const copy = (q: string) => void copyToClipboard(new URL(linkHref(q), location.origin).toString());
  return (
    <div className="gdetail">
      <div className="gdetail-bar">
        <Button variant="quiet" icon={ArrowLeft} onClick={onBack}>
          All cards
        </Button>
        {scene ? (
          <span className="gdetail-scene ba-type-small" style={{ ['--sc' as string]: `var(--sc-${scene.id})` }}>
            <span className="dot" aria-hidden="true" />
            {scene.name}
          </span>
        ) : null}
      </div>
      <div className="gdetail-body">
        <figure className={`gdetail-media ${it.fit === 'contain' ? 'contain' : ''}`}>
          {m.kind === 'video' ? (
            <video key={it.id} src={mediaUrl(g, m.hd)} poster={m.poster ? mediaUrl(g, m.poster) : undefined} controls autoPlay muted loop={it.loop !== false} playsInline preload="metadata" />
          ) : (
            <img key={it.id} src={mediaUrl(g, m.hd)} alt={it.title} decoding="async" />
          )}
        </figure>
        <div className="gdetail-copy">
          <h3 className="ba-type-heading">{it.title}</h3>
          <p className="ba-type-body gdetail-line">{it.line}</p>
          <p className="ba-type-caption gdetail-methods">
            <span className="caps">Methods</span> {it.methods} · {KIND_LABEL[it.kind]}
          </p>
          <div className="gdetail-actions">
            {first ? (
              <>
                <Button variant="primary" icon={it.state === 'app' ? ExternalLink : undefined} onClick={() => openLink(first.q)}>
                  {first.label}
                </Button>
                <IconButton size="md" icon={Link} label="Copy a link to this state" onClick={() => copy(first.q)} />
              </>
            ) : null}
          </div>
          {rest.length ? (
            <ul className="gdetail-links">
              {rest.map((l) => (
                <li key={l.q}>
                  <button type="button" className="link-btn ba-type-small" onClick={() => openLink(l.q)}>
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <p className="ba-type-caption muted gdetail-state">
            {!first ? 'Its scene is on its way.' : it.state === 'app' ? 'The app opens this state.' : it.state === 'partly' ? 'The app has part of this; the link opens the nearest state.' : it.state === 'render' ? 'A film or still; the link opens the scene it belongs to.' : 'A figure; the link opens the scene it belongs to.'}
          </p>
        </div>
      </div>
    </div>
  );
}

/** Where the sheet was when it closed: its chip, search and scroll, kept for the same scene (ba-look's note). */
let remembered: { scenario: ScenarioId; scene: ScenarioId | 'all'; q: string; scrollTop: number } | null = null;

/** Under a scene's chip: the scene's own tour (tours.ts sceneTour, its exact cards in the showcase's order), which closes
 *  the sheet and starts at its first stop. The player (Tour.tsx) imports openLink from here, so the start is spelled out. */
function TourRow({ g, scene }: { g: GalleryData; scene: ScenarioId }) {
  const tour = sceneTour(scene, g);
  const sc = SCENARIOS.find((x) => x.id === scene);
  if (!tour || !sc) return null;
  const start = () => {
    const st = tour.steps[0];
    const it = st ? g.items.find((x) => x.id === st.card) : undefined;
    const link = it && st ? linkOf(it, st) : null;
    if (!link) return;
    useAtlas.getState().setTour({ id: scene, step: 0, paused: false });
    openLink(link.q);
  };
  return (
    <p className="gallery-tour ba-type-small muted">
      <Button variant="quiet" icon={Route} onClick={start}>{`Tour ${sc.name}’s ${tour.steps.length} states in the app`}</Button>
      <span>Each opens in place and holds a few seconds; the strip has Back, Pause and Next.</span>
    </p>
  );
}

export function Gallery() {
  const open = useAtlas((s) => s.galleryOpen);
  const wanted = useAtlas((s) => s.galleryItem);
  const setGallery = useAtlas((s) => s.setGallery);
  const scenario = useAtlas((s) => s.scenario);
  const [data, setData] = useState<GalleryData | null>(null);
  const [scene, setScene] = useState<ScenarioId | 'all'>(scenario);
  const [q, setQ] = useState('');
  const [item, setItem] = useState<string | null>(null);
  const sheet = useRef<HTMLDivElement>(null);

  const scroll = useRef<HTMLDivElement>(null);
  const place = useRef({ scene: scenario as ScenarioId | 'all', q: '' });
  place.current = { scene, q };
  useEffect(() => {
    if (!open) return;
    void loadGallery().then(setData);
    // Reopened with G on the same scene, no card asked for: back where it was.
    const back = !wanted && remembered && remembered.scenario === scenario ? remembered : null;
    setScene(back ? back.scene : scenario);
    setQ(back ? back.q : '');
    setItem(wanted);
    // The search field takes the focus (typing filters at once); a card's detail has none, so the sheet does.
    (sheet.current?.querySelector<HTMLInputElement>('input[type="search"]') ?? sheet.current)?.focus();
    return () => {
      remembered = { scenario, ...place.current, scrollTop: scroll.current?.scrollTop ?? 0 };
    };
  }, [open, scenario, wanted]);
  useEffect(() => {
    // Once the cards are in, the scroll goes back to where it was.
    if (open && data && !item && remembered?.scenario === scenario && scroll.current) scroll.current.scrollTop = remembered.scrollTop;
  }, [open, data, item, scenario]);

  const items = useMemo(() => {
    if (!data) return [];
    const list = data.items.filter((it) => (scene === 'all' || it.scene === scene) && matches(it, q));
    return scene === 'all' ? list : [...list].sort((a, b) => RANK[a.state] - RANK[b.state]);
  }, [data, scene, q]);
  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const it of data?.items ?? []) if (it.scene) out[it.scene] = (out[it.scene] ?? 0) + 1;
    return out;
  }, [data]);

  if (!open) return null;
  const close = () => setGallery(false);
  const current = item ? (data?.items.find((it) => it.id === item) ?? null) : null;
  return (
    <div className="sheet-overlay" onClick={close} role="presentation">
      <div
        ref={sheet}
        tabIndex={-1}
        className="sheet gallery"
        role="dialog"
        aria-modal="true"
        aria-label="Gallery"
        data-label-exclude=""
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          // Esc closes from the search field too, where the app's shortcuts stand back for typing.
          if (e.key === 'Escape') {
            e.stopPropagation();
            close();
          }
        }}
      >
        <header className="sheet-head gallery-head">
          <div className="gallery-title">
            <h2>Gallery</h2>
            <p className="ba-type-small muted">Every shot and result from the project, beside the scene it belongs to.</p>
          </div>
          {!current ? (
            <label className="gallery-search">
              <Search size={14} />
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the gallery" aria-label="Search the gallery" />
            </label>
          ) : null}
          <IconButton size="sm" icon={X} label="Close" keys={['Esc']} onClick={close} />
        </header>
        {current && data ? (
          <Detail g={data} it={current} onBack={() => setItem(null)} />
        ) : (
          <>
            <div className="gallery-chips" role="radiogroup" aria-label="Scene">
              {SCENARIOS.map((sc) => (
                <button key={sc.id} type="button" role="radio" aria-checked={scene === sc.id} className={`gchip ${scene === sc.id ? 'on' : ''}`} style={{ ['--sc' as string]: `var(--sc-${sc.id})` }} onClick={() => setScene(sc.id)}>
                  <span className="dot" aria-hidden="true" />
                  {sc.name}
                  {counts[sc.id] ? <span className="gchip-n">{counts[sc.id]}</span> : null}
                </button>
              ))}
              <button type="button" role="radio" aria-checked={scene === 'all'} className={`gchip ${scene === 'all' ? 'on' : ''}`} onClick={() => setScene('all')}>
                All
                {data ? <span className="gchip-n">{data.items.length}</span> : null}
              </button>
            </div>
            {data && scene !== 'all' && !q ? <TourRow g={data} scene={scene} /> : null}
            <div ref={scroll} className="gallery-scroll">
              {!data ? (
                <p className="gallery-empty ba-type-body muted">Loading the gallery</p>
              ) : items.length ? (
                <ul className="ggrid">
                  {items.map((it) => (
                    <Card key={it.id} g={data} it={it} onOpen={() => setItem(it.id)} />
                  ))}
                </ul>
              ) : (
                <p className="gallery-empty ba-type-body muted">{`No card matches “${q}”`}</p>
              )}
            </div>
          </>
        )}
        <p className="sheet-foot muted">
          A card that reads <span className="gstate gstate-app">In the app</span> opens its exact state. Films and figures play here, each with the model and data behind it.{' '}
          <span className="gallery-foot-keys">
            <Kbd>G</Kbd> opens and closes the gallery.
          </span>
        </p>
      </div>
    </div>
  );
}
